import { useNavigate } from "react-router-dom";
import { Fragment, useState } from "react";

import moment from "moment";
import {
  Button,
  Chip,
  Stack,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import { KeyboardArrowLeft } from "@mui/icons-material";

import conf from "../../conf";
import Spinner from "../utils/Spinner";
import { useAuth } from "../auth/AuthProvider";
import { RequestSchema } from "../../hooks/proxy/requests";
import LinearIndeterminate from "../utils/LinearLoader";
import {
  AxiosMutationRequest,
  useProxyRequestDetail,
} from "../../hooks/proxy/requests";

interface TabPanelProps {
  index: number;
  value: number;
  children?: React.ReactNode;
}

export const a11yProps = (index: number) => {
  return {
    id: `request-tab-${index}`,
    "aria-controls": `request-tabpanel-${index}`,
  };
};

export const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      id={`request-tabpanel-${index}`}
      role="requestpanel"
      hidden={value !== index}
      aria-labelledby={`request-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export const PendingRow = (props: {
  reject: AxiosMutationRequest;
  approve: AxiosMutationRequest;
  request: RequestSchema;
  defaultOpen: boolean;
}) => {
  const { reject, approve, request, defaultOpen } = props;
  const { id, requester, subject, project, updatedAt, status } = request;

  const { user } = useAuth();
  const showEdit = ["org.project.manager"].includes(
    user.activeOrganization.role
  );
  const disableActions = requester === user.email;

  const [comment, setComment] = useState("");

  const detail = useProxyRequestDetail({ id });
  const handleReject = () => reject.mutate({ id, comment });
  const handleApprove = () => approve.mutate({ id, comment });

  const navigate = useNavigate();

  const handleNavigation = () => {
    if (window.history.state && window.history.state.idx > 0) {
      // go back to previous page (if exists)
      navigate(-1);
    } else {
      // otherwise, navigate to a specific page
      navigate("/requests/pending");
    }
  };

  return (
    <Fragment>
      <TableRow key={id} sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
        <TableCell>
          <Button key="previous" color="inherit" onClick={handleNavigation}>
            <KeyboardArrowLeft />
          </Button>
        </TableCell>

        <TableCell>
          <Chip color="warning" label={status} />
        </TableCell>

        <TableCell align="right">{requester}</TableCell>

        <TableCell align="right">{subject}</TableCell>

        <TableCell align="right">
          <b>{project.name ?? "--"}</b>
        </TableCell>

        <TableCell align="right">{moment(updatedAt).fromNow()}</TableCell>
      </TableRow>

      {defaultOpen && (
        <TableRow key={`${id}-detail`} sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
          <TableCell colSpan={7}>
            <Box>
              <TextField
                multiline
                fullWidth
                aria-readonly
                margin="normal"
                variant="filled"
                InputProps={{ readOnly: true }}
                defaultValue={detail?.data?.data.message}
              />

              {showEdit && (
                <Stack
                  spacing={2}
                  marginTop={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="end"
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Comment (optional)"
                    value={comment}
                    disabled={disableActions}
                    onChange={(e) => setComment(e.target.value)}
                  />

                  <Button
                    fullWidth
                    color="success"
                    variant="contained"
                    onClick={handleApprove}
                    disabled={disableActions}
                  >
                    {approve.isPending ? <Spinner sx={{ mx: 4 }} /> : "Approve"}
                  </Button>

                  <Button
                    fullWidth
                    color="error"
                    variant="contained"
                    onClick={handleReject}
                    disabled={disableActions}
                  >
                    {reject.isPending ? <Spinner sx={{ mx: 4 }} /> : "Reject"}
                  </Button>
                </Stack>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

export const ClosedRow = (props: {
  request: RequestSchema;
  defaultOpen: boolean;
}) => {
  const { id, status, subject, project, approver, requester, updatedAt } =
    props.request;

  const navigate = useNavigate();

  const handleNavigation = () => {
    if (window.history.state && window.history.state.idx > 0) {
      // go back to previous page (if exists)
      navigate(-1);
    } else {
      // otherwise, navigate to a specific page
      navigate("/requests/pending");
    }
  };

  return (
    <Fragment>
      <TableRow key={id} sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
        <TableCell>
          <Button key="previous" color="inherit" onClick={handleNavigation}>
            <KeyboardArrowLeft />
          </Button>
        </TableCell>

        <TableCell>
          <Chip
            label={status}
            color={status === "approved" ? "success" : "error"}
          />
        </TableCell>

        <TableCell align="right">{requester || "--"}</TableCell>

        <TableCell align="right">{approver || "--"}</TableCell>

        <TableCell align="right">{subject || "--"}</TableCell>

        <TableCell align="right">
          <b>{project.name || "--"}</b>
        </TableCell>

        <TableCell align="right">
          {new Date(updatedAt).toLocaleString() || "--"}
        </TableCell>
      </TableRow>

      <RequestDetail id={id} />
    </Fragment>
  );
};

export const RequestDetail = ({ id }: { id: string }) => {
  const detail = useProxyRequestDetail({ id });

  return (
    <TableRow key={`${id}-detail`} sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
      {detail.isLoading ? (
        <TableCell colSpan={7}>
          <LinearIndeterminate />
        </TableCell>
      ) : detail.isError ? (
        <TableCell colSpan={7} align="center">
          {detail.error.response?.data.message}
        </TableCell>
      ) : (
        <TableCell colSpan={7}>
          <Box>
            <TextField
              multiline
              fullWidth
              aria-readonly
              margin="normal"
              label="Detail"
              InputProps={{ readOnly: true }}
              defaultValue={detail?.data?.data.message}
            />

            <TextField
              size="small"
              label="Comment"
              InputProps={{ readOnly: true }}
              defaultValue={detail?.data?.data.comment}
              fullWidth
              margin="normal"
            />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

export const PendingTableHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell />

      <TableCell>
        <b>Status</b>
      </TableCell>

      <TableCell align="right">
        <b>Created by</b>
      </TableCell>

      <TableCell align="right">
        <b>Subject</b>
      </TableCell>

      <TableCell align="right">
        <b>Project</b>
      </TableCell>

      <TableCell align="right">
        <b>Issued</b>
      </TableCell>
    </TableRow>
  </TableHead>
);

export const ClosedTableHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell />

      <TableCell>
        <b>Status</b>
      </TableCell>

      <TableCell align="right">
        <b>Created by</b>
      </TableCell>

      <TableCell align="right">
        <b>Approved by</b>
      </TableCell>

      <TableCell align="right">
        <b>Subject</b>
      </TableCell>

      <TableCell align="right">
        <b>Project</b>
      </TableCell>

      <TableCell align="right">
        <b>Issued</b>
      </TableCell>
    </TableRow>
  </TableHead>
);
